
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { format, parseISO } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { sendDevotional } from "@/utils/sendDevotional";
import { useIsMobile } from "@/hooks/use-mobile";

const Archive = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const { data: devotionals = [], isLoading } = useQuery({
    queryKey: ["archiveDevotionals"],
    queryFn: async () => {
      const currentDate = format(new Date(), "yyyy-MM-dd");
      
      const { data, error } = await supabase
        .from("devotionals")
        .select("*")
        .lte("date", currentDate)
        .order("date", { ascending: false });

      if (error) {
        console.error("Error fetching devotionals:", error);
        return [];
      }

      return data || [];
    },
  });

  const handleDevotionalClick = async (date: string) => {
    // Ensure proper date handling, especially on mobile
    const devotionalDate = parseISO(date);

    // If it's January 1st, send the devotional
    if (format(devotionalDate, 'yyyy-MM-dd') === '2024-01-01') {
      try {
        await sendDevotional('2024-01-01');
        toast({
          title: "Devotional Sent",
          description: "The January 1st devotional has been sent to your email.",
        });
      } catch (error: any) {
        console.error('Error sending devotional:', error);
        toast({
          title: "Error",
          description: error.message || "Failed to send devotional. Please try again.",
          variant: "destructive",
        });
      }
    }

    // Use consistent date formatting when navigating
    navigate(`/devotional/${format(devotionalDate, "yyyy-MM-dd")}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sage-50 to-white flex items-center justify-center">
        <p className="text-xl text-sage-600">Loading archive...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sage-50 to-white p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-serif font-bold text-sage-800 mb-8">All Devotionals</h1>
        <div className="space-y-4">
          {devotionals.map((devotional, index) => {
            return (
              <Card key={index} className="p-4 hover:bg-sage-50 transition-colors">
                <Button
                  variant="ghost"
                  className="w-full flex justify-between items-center text-left"
                  onClick={() => handleDevotionalClick(devotional.date)}
                >
                  <div>
                    <p className="text-lg font-serif text-sage-800">{devotional.topic}</p>
                    <p className="text-sage-600">
                      {format(parseISO(devotional.date), 'MMMM d, yyyy')}
                    </p>
                  </div>
                  <ChevronRight className="text-sage-500" />
                </Button>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Archive;
